import { useState, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { loadOrders, STATUSES } from "../../data/adminData";
import { fmt } from "../../data/store";

const COLORS = { "Jauns": "bg-[#D0121A] text-white", "Apmaksāts": "bg-[#111111] text-white", "Nosūtīts": "bg-[#E5A000] text-white", "Izpildīts": "bg-[#1E9E45] text-white", "Atcelts": "bg-[#E2E2E2] text-[#6B6B6B]" };
const DELIVERY = { kurjers: "Kurjers", pakomats: "Pakomāts", veikals: "Demo veikals" };
const PAYMENT = { banka: "Internetbanka", karte: "Karte", rekins: "Rēķins" };

export const StatusBadge = ({ status }) => (
  <span className={`inline-block text-[11px] font-bold px-2 py-0.5 ${COLORS[status] || "bg-[#E2E2E2]"}`}>{status}</span>
);

export default function Orders() {
  const [orders, setOrders] = useState(() => loadOrders());
  const [filter, setFilter] = useState("visi");
  const [open, setOpen] = useState(null);
  const shown = useMemo(() => (filter === "visi" ? orders : orders.filter((o) => o.status === filter)), [orders, filter]);

  const setStatus = (nr, status) => setOrders((prev) => prev.map((o) => (o.nr === nr ? { ...o, status } : o)));

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h1 className="text-2xl font-extrabold uppercase tracking-tight">Pasūtījumi ({shown.length})</h1>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="orders-status-filter"
          className="text-sm border border-[#E2E2E2] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#111111]">
          <option value="visi">Visi statusi</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="bg-white border border-[#E2E2E2]">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#6B6B6B] border-b border-[#E2E2E2]">
            <th className="p-3">Nr.</th><th>Klients</th><th className="hidden md:table-cell">Uzņēmums</th><th className="hidden sm:table-cell">Datums</th><th>Statuss</th><th className="text-right">Summa</th><th></th>
          </tr></thead>
          <tbody>
            {shown.map((o) => (
              <FragmentRow key={o.nr} o={o} open={open === o.nr} onToggle={() => setOpen(open === o.nr ? null : o.nr)} setStatus={setStatus} />
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <p className="p-6 text-sm text-[#6B6B6B] text-center">Nav pasūtījumu ar šo statusu.</p>}
      </div>
    </div>
  );
}

const FragmentRow = ({ o, open, onToggle, setStatus }) => (
  <>
    <tr className="border-b border-[#F5F5F5] hover:bg-[#F5F5F5] cursor-pointer" onClick={onToggle} data-testid={`order-row-${o.nr}`}>
      <td className="p-3 font-bold">{o.nr}</td>
      <td>{o.customer || "—"}<div className="text-xs text-[#6B6B6B]">{o.email}</div></td>
      <td className="hidden md:table-cell text-[#6B6B6B]">{o.company || "Privātpersona"}</td>
      <td className="hidden sm:table-cell text-[#6B6B6B]">{new Date(o.date).toLocaleDateString("lv-LV")}</td>
      <td><StatusBadge status={o.status} /></td>
      <td className="text-right font-bold">{fmt(o.total)}</td>
      <td className="p-3 text-[#6B6B6B]">{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</td>
    </tr>
    {open && (
      <tr className="bg-[#F5F5F5]">
        <td colSpan={7} className="p-4" data-testid={`order-detail-${o.nr}`}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-[#E2E2E2] p-4">
              <h4 className="text-xs font-bold uppercase tracking-widest mb-3">Preces</h4>
              {o.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5 border-b border-[#F5F5F5]">
                  <span>{it.name} <span className="text-xs text-[#6B6B6B]">({it.sku})</span></span>
                  <span className="font-semibold shrink-0 ml-3">{it.qty} × {fmt(it.price)}</span>
                </div>
              ))}
              <div className="flex justify-between font-extrabold pt-2 mt-1"><span>Kopā (ar PVN)</span><span>{fmt(o.total)}</span></div>
            </div>
            <div className="bg-white border border-[#E2E2E2] p-4 space-y-2 text-sm">
              <h4 className="text-xs font-bold uppercase tracking-widest mb-1">Detaļas</h4>
              <div><span className="text-[#6B6B6B]">Piegāde:</span> <span className="font-semibold">{DELIVERY[o.delivery] || o.delivery}</span></div>
              <div><span className="text-[#6B6B6B]">Apmaksa:</span> <span className="font-semibold">{PAYMENT[o.payment] || o.payment}</span></div>
              <label className="block pt-2">
                <span className="text-xs font-bold uppercase tracking-widest">Mainīt statusu</span>
                <select value={o.status} onChange={(e) => setStatus(o.nr, e.target.value)} onClick={(e) => e.stopPropagation()} data-testid={`order-status-select-${o.nr}`}
                  className="mt-1 w-full text-sm border border-[#E2E2E2] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#111111]">
                  {["Jauns", "Apmaksāts", "Nosūtīts", "Izpildīts", "Atcelts"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </label>
            </div>
          </div>
        </td>
      </tr>
    )}
  </>
);
